import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import eabLogo from "@/assets/eab-logo-horizontal.png";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { z } from "zod";
import { Loader2, Users, Briefcase, Mail, CheckCircle } from "lucide-react";
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { passwordSchema } from "@/lib/passwordValidation";
import { PasswordRequirements } from "@/components/auth/PasswordRequirements";

const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(1, "Password is required")
});

const signupSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: passwordSchema,
  fullName: z.string().min(2, "Name must be at least 2 characters"),
  role: z.enum(["entrepreneur", "mentor"]),
  privacyPolicy: z.literal(true, {
    errorMap: () => ({
      message: "You must agree to the Privacy Policy"
    })
  })
});
export default function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    user,
    signIn,
    signUp,
    resetPassword,
    loading: authLoading
  } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("login");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState<"entrepreneur" | "mentor">("entrepreneur");
  const [privacyPolicy, setPrivacyPolicy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setErrors({
        email: "Please enter your email address"
      });
      return;
    }
    setIsLoading(true);
    const {
      error
    } = await resetPassword(email);
    setIsLoading(false);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Password reset email sent! Check your inbox.");
      setShowForgotPassword(false);
    }
  };
  // RequireRole sends unauthenticated visitors here as
  // /auth?redirect=<original path>, so emailed deep links (e.g.
  // /schedule/:requestId) still land correctly after sign-in.
  const redirectTarget = (() => {
    const raw = searchParams.get("redirect");
    // Only allow internal paths — never an absolute URL (open-redirect guard).
    if (!raw || !raw.startsWith("/") || raw.startsWith("//")) return "/dashboard";
    return raw;
  })();

  useEffect(() => {
    if (user && !authLoading) {
      navigate(redirectTarget, { replace: true });
    }
  }, [user, authLoading, navigate, redirectTarget]);
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "signup") setActiveTab("signup");
    
    const roleParam = searchParams.get("role");
    if (roleParam === "mentor" || roleParam === "entrepreneur") {
      setRole(roleParam);
    }
  }, [searchParams]);
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    try {
      loginSchema.parse({
        email,
        password
      });
    } catch (err) {
      if (err instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        err.errors.forEach(e => {
          if (e.path[0]) fieldErrors[e.path[0].toString()] = e.message;
        });
        setErrors(fieldErrors);
        return;
      }
      // Any NON-Zod throw previously fell through to signUp()/signIn() with
      // unvalidated input, bypassing the client password policy and the Privacy
      // Policy checkbox (which has no server-side counterpart). Fail closed.
      console.error("Validation failed:", err);
      toast.error("We couldn't validate your details. Please check and try again.");
      return;
    }
    setIsLoading(true);
    const {
      error
    } = await signIn(email, password);
    setIsLoading(false);
    if (error) {
      if (error.message.includes("Email not confirmed")) {
        toast.error("Please verify your email address before signing in.");
      } else if (error.message.includes("Invalid login credentials")) {
        toast.error("Invalid email or password. Please try again.");
      } else {
        toast.error(error.message);
      }
    } else {
      toast.success("Welcome back!");
      navigate(redirectTarget, { replace: true });
    }
  };
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    try {
      signupSchema.parse({
        email,
        password,
        fullName,
        role,
        privacyPolicy
      });
    } catch (err) {
      if (err instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        err.errors.forEach(e => {
          if (e.path[0]) fieldErrors[e.path[0].toString()] = e.message;
        });
        setErrors(fieldErrors);
        return;
      }
      // Any NON-Zod throw previously fell through to signUp()/signIn() with
      // unvalidated input, bypassing the client password policy and the Privacy
      // Policy checkbox (which has no server-side counterpart). Fail closed.
      console.error("Validation failed:", err);
      toast.error("We couldn't validate your details. Please check and try again.");
      return;
    }
    setIsLoading(true);
    const {
      error
    } = await signUp(email, password, fullName, role);
    setIsLoading(false);
    if (error) {
      // Show the full error message (includes helpful info about resent confirmation emails)
      toast.error(error.message);
    } else {
      setShowEmailConfirmation(true);
    }
  };
  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-secondary">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>;
  }
  return (
    <>
    <AlertDialog open={showEmailConfirmation} onOpenChange={setShowEmailConfirmation}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader className="text-center sm:text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Mail className="h-8 w-8 text-primary" />
          </div>
          <AlertDialogTitle className="text-2xl">Registration Successful!</AlertDialogTitle>
          <AlertDialogDescription className="text-base space-y-4">
            <div className="flex items-start gap-2 mt-4">
              <CheckCircle className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
              <p>Your account has been created. Please check your email to confirm your address.</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-4 text-left">
              <p className="font-medium text-foreground mb-2">Didn't receive the email within 15 minutes?</p>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li>Check your spam or junk folder first</li>
                <li>Make sure you entered the correct email address</li>
                <li>Try signing up again if the issue persists</li>
              </ul>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="sm:justify-center">
          <Button onClick={() => {
            setShowEmailConfirmation(false);
            setActiveTab("login");
          }} className="w-full sm:w-auto">
            Got it, I'll check my email
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <div className="min-h-screen bg-secondary p-4 relative">
      {/* Top left logo */}
      <Link to="/" className="absolute top-6 left-6">
        <img src={eabLogo} alt="EAB Connect" className="h-10" />
      </Link>
      
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <p className="text-muted-foreground">Start - Grow - Thrive</p>
          </div>

          <Card className="shadow-lg">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">Sign In</TabsTrigger>
              <TabsTrigger value="signup">Create Account</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              {showForgotPassword ? <form onSubmit={handleForgotPassword}>
                  <CardHeader>
                    <CardTitle>Reset Password</CardTitle>
                    <CardDescription>Enter your email to receive a password reset link</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="reset-email">Email</Label>
                      <Input id="reset-email" type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} className={errors.email ? "border-destructive" : ""} />
                      {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                    </div>
                  </CardContent>
                  <CardFooter className="flex flex-col gap-2">
                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      Send Reset Link
                    </Button>
                    <Button type="button" variant="ghost" className="w-full" onClick={() => setShowForgotPassword(false)}>
                      Back to Sign In
                    </Button>
                  </CardFooter>
                </form> : <form onSubmit={handleLogin}>
                  <CardHeader>
                    <CardTitle>Welcome Back</CardTitle>
                    <CardDescription>Sign in to your account to continue</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input id="login-email" type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} className={errors.email ? "border-destructive" : ""} />
                      {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="login-password">Password</Label>
                        <button type="button" onClick={() => setShowForgotPassword(true)} className="text-sm text-primary hover:underline">
                          Forgot password?
                        </button>
                      </div>
                      <Input id="login-password" type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} className={errors.password ? "border-destructive" : ""} />
                      {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      Sign In
                    </Button>
                  </CardFooter>
                </form>}
            </TabsContent>

            <TabsContent value="signup">
              <form onSubmit={handleSignup}>
                <CardHeader>
                  <CardTitle>Create Account</CardTitle>
                  <CardDescription>Join our community of entrepreneurs and mentors</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name">Full Name</Label>
                    <Input id="signup-name" placeholder="John Doe" value={fullName} onChange={e => setFullName(e.target.value)} className={errors.fullName ? "border-destructive" : ""} />
                    {errors.fullName && <p className="text-sm text-destructive">{errors.fullName}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <Input id="signup-email" type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} className={errors.email ? "border-destructive" : ""} />
                    {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <Input id="signup-password" type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} className={errors.password ? "border-destructive" : ""} />
                    <PasswordRequirements password={password} />
                    {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                  </div>
                  <div className="space-y-3">
                    <Label>I am a...</Label>
                    <RadioGroup value={role} onValueChange={v => setRole(v as "entrepreneur" | "mentor")} className="grid grid-cols-2 gap-4">
                      <div>
                        <RadioGroupItem value="entrepreneur" id="entrepreneur" className="peer sr-only" />
                        <Label htmlFor="entrepreneur" className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-popover p-4 hover:bg-accent/5 hover:text-accent-foreground peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 cursor-pointer transition-colors">
                          <Briefcase className="mb-2 h-6 w-6" />
                          <span className="text-sm font-medium">Entrepreneur</span>
                        </Label>
                      </div>
                      <div>
                        <RadioGroupItem value="mentor" id="mentor" className="peer sr-only" />
                        <Label htmlFor="mentor" className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-popover p-4 hover:bg-accent/5 hover:text-accent-foreground peer-data-[state=checked]:border-accent peer-data-[state=checked]:bg-accent/5 cursor-pointer transition-colors">
                          <Users className="mb-2 h-6 w-6" />
                          <span className="text-sm font-medium">Mentor</span>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Checkbox id="privacy-policy" checked={privacyPolicy} onCheckedChange={checked => setPrivacyPolicy(checked === true)} className={errors.privacyPolicy ? "border-destructive" : ""} />
                    <div className="grid gap-1.5 leading-none">
                      <Label htmlFor="privacy-policy" className="text-sm font-normal cursor-pointer">
                        I agree to the{" "}
                        <Link to="/privacy-policy" target="_blank" className="text-primary hover:underline font-medium">
                          Privacy Policy
                        </Link>
                      </Label>
                      {errors.privacyPolicy && <p className="text-sm text-destructive">{errors.privacyPolicy}</p>}
                    </div>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button type="submit" className="w-full bg-accent hover:bg-accent/90" disabled={isLoading}>
                    {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Create Account
                  </Button>
                </CardFooter>
              </form>
            </TabsContent>
          </Tabs>
          </Card>
        </div>
      </div>
    </div>
    </>
  );
}